import { IPagination, IPaginationRequest } from '@simOn/common/http';
import { TransformationInterface } from '@simOn/common/matterport';
import { Attachment, FileShort, LastEditedType } from '@simOn/common/models';
import { UserInfoCard } from '@simOn/common/user-info-card-models';
import { TSelectStatus } from '@simOn/ui/sim-select';
import { EnumValueGetter } from '@simOn/utils/models';

export type TTicketStatus = 'Unknown' | 'Open' | 'InProgress' | 'Closed';
export type TTicketActivityType = 'Unknown' | 'Comment' | 'ActivityStatus';
export type TTicketActivity = ITicketComment | ITicketActivityStatus;
export type TicketPaginated = IPagination<TicketPaginationItem>;

export interface Ticket extends TicketMonitor {
  id: string;
  name: string;
  htmlText: string;
  dueDate: string | undefined;
  status: TTicketStatus;
  activities: TTicketActivity[];
  attachments: AttachmentCopy[];
  assignments: UserInfoCard[];
  transformation?: TransformationInterface;
  positionDeleted: boolean;
  hasUpdates: boolean;
  containsBlockedAttachments: boolean;
}

export interface ITicketShort {
  id: string;
  name: string;
  transformation: TransformationInterface | undefined;
  positionDeleted: boolean;
  hasUpdates: boolean;
  containsBlockedAttachments: boolean;
}

export interface TicketPaginationItem extends TicketMonitor {
  id: string;
  name: string;
  status: TTicketStatus;
  dueDate: string | undefined;
  transformation?: TransformationInterface;
  hasUpdates: boolean;
  positionDeleted: boolean;
  stakeholders: UserInfoCard[];
}

export interface TicketMonitor {
  createdAt: string;
  author?: UserInfoCard;
  editedAt?: string;
  editor?: UserInfoCard;
}

export interface IAttachment {
  id: string;
  blobPath: string;
  name: string;
  isBlockedBySubscriptionLimit: boolean;
}

export interface ITicketAssignment {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
}

export interface ITicketActivity {
  id: string;
  ticketId: string;
  createdTimestamp: string;
  userEmail: string;
  userId: string;
  userName: string;
  activityType: TTicketActivityType;
}

export interface ITicketComment extends ITicketActivity {
  htmlText: string;
  isAuthor: boolean;
  isDeleted: boolean;
}

export interface ITicketActivityStatus extends ITicketActivity {
  status: TTicketStatus;
}

export type TUpdateTicketForm = Pick<ITicketDetails, 'assigneeIds' | 'name' | 'htmlText' | 'dueDate'>;

export interface IAddTicketComment {
  ticketId: string;
  htmlText: string;
}

export interface ITicketStatusUpdate {
  ticketId: string;
  status: TTicketStatus;
}

export interface ICreateTicket extends Omit<ITicketDetails, 'attachments'> {
  attachments: FileCopy[];
}

export interface IUpdateTicket extends ITicketDetails {
  ticketId: string;
  positionDeleted: boolean;
  status: TTicketStatus;
}

export interface ITicketDetails {
  htmlText: string;
  name: string;
  transformation: Omit<TransformationInterface, 'normal' | 'marker' | 'scale'>;
  assigneeIds: string[];
  dueDate: string | undefined;
  attachments: FileShort[];
}

export interface AttachmentCopy extends Attachment, TicketFileCopy {}
export interface FileCopy extends FileShort, TicketFileCopy {}

export interface TicketFileCopy {
  isCopy?: boolean;
}
export interface ITicketStatusDataBase {
  label: string;
  value: TTicketStatus | undefined;
  icon: string;
}
export interface ITicketStatusDataSelect extends ITicketStatusDataBase {
  selectStatus: TSelectStatus;
  dueDateColor: string;
}

export const TICKET_STATUS_COLLECTION: ITicketStatusDataBase[] = [
  {
    label: $localize`:@@3D_TICKET_MODAL_STATUSES_OPEN:Open`,
    value: 'Open',
    icon: 'start'
  },
  {
    label: $localize`:@@3D_TICKET_MODAL_STATUSES_IN_PROGRESS:In progress`,
    value: 'InProgress',
    icon: 'recurring'
  },
  {
    label: $localize`:@@3D_TICKET_MODAL_STATUSES_CLOSED:Closed`,
    value: 'Closed',
    icon: 'check_outline'
  }
];

export const TICKET_STATUS_BASE: Record<TTicketStatus, ITicketStatusDataBase> = {
  Unknown: {
    label: '',
    value: undefined,
    icon: ''
  },
  Open: {
    label: $localize`:@@3D_TICKET_MODAL_STATUSES_OPEN:Open`,
    value: 'Open',
    icon: 'start'
  },
  InProgress: {
    label: $localize`:@@3D_TICKET_MODAL_STATUSES_IN_PROGRESS:In progress`,
    value: 'InProgress',
    icon: 'recurring'
  },
  Closed: {
    label: $localize`:@@3D_TICKET_MODAL_STATUSES_CLOSED:Closed`,
    value: 'Closed',
    icon: 'check_outline'
  }
};

export const TICKET_STATUS_SELECT: Record<TTicketStatus, ITicketStatusDataSelect> = {
  Unknown: {
    ...{
      selectStatus: 'info',
      dueDateColor: ''
    },
    ...TICKET_STATUS_BASE.Unknown
  },
  Open: {
    ...{
      selectStatus: 'info',
      dueDateColor: 'var(--status-info-due-date, #4670DB)'
    },
    ...TICKET_STATUS_BASE.Open
  },
  InProgress: {
    ...{
      selectStatus: 'warning',
      dueDateColor: 'var(--in-progress-text, #906500)'
    },
    ...TICKET_STATUS_BASE.InProgress
  },
  Closed: {
    ...{
      selectStatus: 'success',
      dueDateColor: 'var(--status-success-default, #079E58)'
    },
    ...TICKET_STATUS_BASE.Closed
  }
};

export interface TicketListFilters {
  statuses: string[];
  dueDate: DueDateType | undefined;
  authorIds: string[];
  stakeholderIds: string[];
  lastEdited: LastEditedType | undefined;
  assignedOnly: boolean;
  dueDateFrom: string | undefined;
  dueDateTo: string | undefined;
  editedAtFrom: string | undefined;
  editedAtTo: string | undefined;
  sortBy: TicketsSortBy;
  isAscending: boolean;
  name: string | undefined | null;
}

export type TicketListFiltersToCount = Pick<
  TicketListFilters,
  'statuses' | 'dueDate' | 'authorIds' | 'stakeholderIds' | 'lastEdited' | 'assignedOnly'
>;

export interface TicketListFiltersWithPage extends TicketListFilters, IPaginationRequest {}

export type DueDateType = (typeof TicketDueDate)[keyof typeof TicketDueDate];

export const TicketDueDate = {
  next12Hours: 12,
  next24Hours: 24,
  nextWeek: 168,
  nextMonth: 720,
  outdated: 1,
  custom: 0
} as const;

export const TICKET_DUE_DATE_COLLECTION: { name: string; value: DueDateType }[] = [
  { name: $localize`:@@NEXT_12_HOURS:Next 12 hours`, value: TicketDueDate.next12Hours },
  { name: $localize`:@@NEXT_24_HOURS:Next 24 hours`, value: TicketDueDate.next24Hours },
  { name: $localize`:@@NEXT_WEEK:Next week`, value: TicketDueDate.nextWeek },
  { name: $localize`:@@NEXT_MONTH:Next month`, value: TicketDueDate.nextMonth },
  { name: $localize`:@@OUTDATED:Outdated`, value: TicketDueDate.outdated },
  { name: $localize`:@@SPACE_SETTINGS_ACTIVITY_LOG_TIME_CUSTOM:Custom`, value: TicketDueDate.custom }
];

export const TicketsSortBy = {
  None: 0,
  Name: 1,
  DueDate: 2,
  CreatedAt: 3,
  EditedAt: 4
} as const;

export type TicketsSortByKeys = keyof typeof TicketsSortBy;
export type TicketsSortByValues = EnumValueGetter<typeof TicketsSortBy>;
export type TicketsSortBy = TicketsSortByValues | TicketsSortByKeys;
